import Phaser from "phaser";
import { bakeTexture, OUTLINE, roundedRectPath } from "./bake.ts";
import { css } from "./color.ts";

export const COIN_RADIUS = 18;

/** Gold coin with an embossed value. `value` 0 draws a star emblem instead (HUD icon). */
export function ensureCoinTexture(scene: Phaser.Scene, value: number): string {
  const R = COIN_RADIUS;
  const size = R * 2 + 4;
  return bakeTexture(scene, `coin-${value}`, size, size, (ctx) => {
    ctx.translate(size / 2, size / 2);

    ctx.fillStyle = css(OUTLINE);
    ctx.beginPath();
    ctx.arc(0, 0, R, 0, Math.PI * 2);
    ctx.fill();

    const rim = ctx.createLinearGradient(-R, -R, R, R);
    rim.addColorStop(0, css(0xfff08a));
    rim.addColorStop(0.5, css(0xffc61a));
    rim.addColorStop(1, css(0xd98e00));
    ctx.fillStyle = rim;
    ctx.beginPath();
    ctx.arc(0, 0, R - 2.2, 0, Math.PI * 2);
    ctx.fill();

    const faceR = R * 0.72;
    const face = ctx.createRadialGradient(-faceR * 0.4, -faceR * 0.4, 1, 0, 0, faceR);
    face.addColorStop(0, css(0xffe676));
    face.addColorStop(1, css(0xf0ad00));
    ctx.fillStyle = face;
    ctx.beginPath();
    ctx.arc(0, 0, faceR, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = css(0xb87400);
    ctx.lineWidth = 1.6;
    ctx.stroke();

    if (value > 0) {
      const label = String(value);
      const fontSize = label.length >= 3 ? 11.5 : label.length === 2 ? 14 : 17;
      ctx.font = `900 ${fontSize}px "Arial Black", Arial, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = css(0xfff6c4, 0.9);
      ctx.fillText(label, 0.7, 1.7);
      ctx.fillStyle = css(0xc27c00);
      ctx.fillText(label, 0, 1);
    } else {
      drawStar(ctx, 0, 0, faceR * 0.62, faceR * 0.28);
      ctx.fillStyle = css(0xc27c00);
      ctx.fill();
    }

    // Shine.
    ctx.strokeStyle = css(0xffffff, 0.75);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, R - 5, Math.PI * 1.1, Math.PI * 1.45);
    ctx.stroke();
  });
}

function drawStar(ctx: CanvasRenderingContext2D, x: number, y: number, outer: number, inner: number): void {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    if (i === 0) ctx.moveTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
    else ctx.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
  }
  ctx.closePath();
}

export const FUEL_CAN_SIZE = { width: 40, height: 46 };

/** Glossy red jerry can (used for the fuel pickup and the HUD fuel icon). */
export function ensureFuelCanTexture(scene: Phaser.Scene): string {
  const { width: W, height: H } = FUEL_CAN_SIZE;
  return bakeTexture(scene, "fuel-can", W, H, (ctx) => {
    const outline = css(OUTLINE);
    const bodyX = 4;
    const bodyY = 11;
    const bodyW = 30;
    const bodyH = 33;

    // Spout + cap (behind the body, top right).
    ctx.save();
    ctx.translate(bodyX + bodyW - 4, bodyY + 2);
    ctx.rotate(0.6);
    ctx.fillStyle = outline;
    roundedRectPath(ctx, -4, -12, 9, 13, 2);
    ctx.fill();
    ctx.fillStyle = css(0x5a5d63);
    roundedRectPath(ctx, -2, -10, 5, 9, 1.5);
    ctx.fill();
    ctx.restore();

    // Carry handle with a cut-out.
    ctx.fillStyle = outline;
    roundedRectPath(ctx, bodyX + 3, bodyY - 9, 18, 13, 4);
    ctx.fill();
    ctx.fillStyle = css(0xd62a24);
    roundedRectPath(ctx, bodyX + 5, bodyY - 7, 14, 9, 3);
    ctx.fill();
    ctx.fillStyle = outline;
    roundedRectPath(ctx, bodyX + 8, bodyY - 4, 8, 4, 2);
    ctx.fill();

    // Body.
    roundedRectPath(ctx, bodyX, bodyY, bodyW, bodyH, 6);
    const grad = ctx.createLinearGradient(bodyX, 0, bodyX + bodyW, 0);
    grad.addColorStop(0, css(0xff6a5c));
    grad.addColorStop(0.45, css(0xe8322a));
    grad.addColorStop(1, css(0xa8171a));
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.strokeStyle = outline;
    ctx.lineWidth = 2.6;
    ctx.stroke();

    // Embossed X panel.
    const px = bodyX + 6;
    const py = bodyY + 6;
    const pw = bodyW - 12;
    const ph = bodyH - 12;
    ctx.strokeStyle = css(0x8f1214, 0.9);
    ctx.lineWidth = 2.2;
    roundedRectPath(ctx, px, py, pw, ph, 3);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(px + 2, py + 2);
    ctx.lineTo(px + pw - 2, py + ph - 2);
    ctx.moveTo(px + pw - 2, py + 2);
    ctx.lineTo(px + 2, py + ph - 2);
    ctx.stroke();
    ctx.strokeStyle = css(0xffb3a8, 0.55);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(px + 3, py + 1);
    ctx.lineTo(px + pw - 1, py + ph - 3);
    ctx.stroke();

    // Gloss highlight.
    ctx.fillStyle = css(0xffffff, 0.45);
    roundedRectPath(ctx, bodyX + 3, bodyY + 4, 3.5, bodyH - 12, 2);
    ctx.fill();
  });
}
